import { Selector, createSelector, createSelectorFactory, resultMemoize } from '@ngrx/store';

import { isDefined } from '@dmb/core';

import { NewTrack } from '../../../model';
import { ContributorAssignmentAndEntry } from '../../../model/product/contributors';
import { CoverType, Validity, getCoverLabel, pricecodeOptions } from '../../../model/product/general';
import { TrackSummary } from '../../../model/product/summary';
import {
  CommonTrackMetadata,
  Explicitness,
  KIPolicies,
  TrackMetadata,
  getKiPoliciesLabels,
} from '../../../model/product/track-metadata';
import { DmbWizardValue, WizardStep, defaultTrackMetadata, initialState } from '../../../model/product/wizard';
import {
  countrySelected,
  genreSelected,
  hasAiUsage,
  hasFrontcover,
  isExplicit,
  isInstrumentalConform,
  isJpeg,
  isJpegOrPdf,
  isNumber,
  isPdf,
  max2Decimals,
  noEmoji,
  notBeforePreOrderDate,
  notEmpty,
  notInThePast,
  requiredAgeRating,
  requiredDate,
  requiredString,
  selectionAvailable,
  seriesNumberOrRange,
  shopSelected,
  staysInSong,
  trackSelected,
  validISRC,
  validIsbnFormat,
  validNumberOfPrereleases,
  validPublisher,
  validTitleVersion,
  wholeNumber,
} from '../../../validators/validators';
import {
  max,
  maxLength,
  min,
  minLength,
  unequalVersionTitle,
  validCatalogNumber,
  validEanUpc,
  validTitle,
} from '../../../validators/validators';
import { ProductType } from '../type/product-type-select.component';
import { errorMessageFor } from '../uploadUtils';
import { combineLists, entryIsValid } from '../utils';
import { wizardFeature } from './app.reducer';

type DeepSelector<StoreState, TargetState> = Selector<StoreState, TargetState> &
  (TargetState extends object
    ? Readonly<{
        [K in keyof TargetState]: TargetState[K] extends object
          ? DeepSelector<StoreState, TargetState[K]>
          : Selector<StoreState, TargetState[K]>;
      }>
    : unknown);

function createNestedSelectors<StoreState, BaseState extends object>(
  baseSelector: Selector<StoreState, BaseState>,
  initialValue: BaseState,
): DeepSelector<StoreState, BaseState> {
  Object.entries(initialValue).forEach(([key, value]) => {
    const newSelector = createSelector(baseSelector, (val) => (val as Record<string, unknown>)[key]);

    Object.defineProperty(baseSelector, key, { value: newSelector });

    if (typeof value === 'object' && value !== null) {
      createNestedSelectors(newSelector, value);
    }
  });

  return baseSelector as DeepSelector<StoreState, BaseState>;
}

export const valueSelectors = createNestedSelectors(wizardFeature.selectValue, initialState.value);
export const constraintSelectors = createNestedSelectors(wizardFeature.selectConstraints, initialState.constraints);

export const selectHasVideo = createSelector(
  valueSelectors.tracklisting,
  () => false, // TODO: update once videos can be added
);

export const storeBusy = createSelector(
  wizardFeature.selectDbLoading,
  wizardFeature.selectTrackDeletePending,
  (dbLoading, trackIds) => dbLoading || trackIds.length > 0,
);

/**
 * Only recreate result if item or a dependency changes.
 * Use this with an array selector, so only items that have to change are actually recreated.
 */
function memoizePerItem<T extends object, D extends readonly unknown[], R>(fn: (item: T, ...deps: D) => R) {
  const cache = new WeakMap<T, { deps: D; result: R }>();

  return (item: T, ...deps: D): R => {
    const cached = cache.get(item);
    if (cached && cached.deps.every((dep, index) => dep === deps[index])) {
      return cached.result;
    }

    const result = fn(item, ...deps);
    cache.set(item, { deps, result });
    return result;
  };
}

export const nonPendingTracks = createSelector(
  valueSelectors.tracklisting,
  wizardFeature.selectTrackDeletePending,
  (tracks, pendingTracks) =>
    pendingTracks.length === 0 ? tracks : tracks.filter((t) => !pendingTracks.includes(t.id)),
);

export type TrackOption =
  | { id: string; title: string; titleVersion: string; existing: false }
  | { id: number; title: string; titleVersion: string; existing: true };

// Don't update result unnecessarily, to avoid rerendering all dropdowns
export const trackDropdownOptions = createSelectorFactory<object, TrackOption[]>((projector) =>
  resultMemoize(
    projector,
    (a: TrackOption[], b: TrackOption[]) =>
      a.length === b.length &&
      a.every(
        (option, index) =>
          option.id === b[index].id &&
          option.title === b[index].title &&
          option.titleVersion === b[index].titleVersion &&
          option.existing === b[index].existing,
      ),
  ),
)(nonPendingTracks, (tracks: ReturnType<typeof nonPendingTracks>): TrackOption[] =>
  tracks.map(({ id, title, titleVersion, existing }) =>
    existing ? { id, title, titleVersion, existing: existing || false } : { id, title, titleVersion, existing: false },
  ),
);

export const selectMainContributors = createSelector(
  valueSelectors.contributors,
  valueSelectors.productContributors,
  (contributors, productAssignments) =>
    productAssignments.map((assignment) => {
      const contributor = contributors.find((c) => c.id === assignment.id);
      if (!contributor) throw new Error('Missing contributor');
      return {
        ...assignment,
        contributor,
      };
    }),
);

const trackWithContributors = memoizePerItem(
  (track: NewTrack, contributors: ReturnType<typeof valueSelectors.contributors>) => ({
    ...track,
    contributors: track.contributors.map((assignment) => {
      const contributor = contributors.find((c) => c.id === assignment.id);
      if (!contributor) throw new Error('Missing contributor');
      return {
        ...assignment,
        contributor,
      };
    }),
  }),
);

export const selectTracksWithContributors = createSelector(
  nonPendingTracks,
  valueSelectors.contributors,
  (tracks, contributors) =>
    tracks.map((track) => (track.existing ? track : trackWithContributors(track, contributors))),
);

export const selectAiUsage = createSelector(nonPendingTracks, selectHasVideo, (tracks, hasVideo) => {
  const allNone = tracks.every(
    (t) =>
      t.existing ||
      t.metadata.aiUsageToggle === false ||
      ((t.metadata.aiUsageAudio === KIPolicies.NONE || t.metadata.aiUsageAudio === '') &&
        (t.metadata.aiUsageComposition === KIPolicies.NONE || t.metadata.aiUsageComposition === '') &&
        (!hasVideo || t.metadata.aiUsageVideo === KIPolicies.NONE || t.metadata.aiUsageVideo === '') &&
        (t.metadata.instrumental || t.metadata.aiUsageLyrics === KIPolicies.NONE || t.metadata.aiUsageLyrics === '')),
  );

  if (allNone) {
    return getKiPoliciesLabels(KIPolicies.NONE);
  } else {
    const allFull = tracks.every(
      (t) =>
        t.existing ||
        t.metadata.aiUsageToggle === false ||
        ((t.metadata.aiUsageAudio === KIPolicies.FULL || t.metadata.aiUsageAudio === '') &&
          (t.metadata.aiUsageComposition === KIPolicies.FULL || t.metadata.aiUsageComposition === '') &&
          (!hasVideo || t.metadata.aiUsageVideo === KIPolicies.FULL || t.metadata.aiUsageVideo === '') &&
          (t.metadata.aiUsageLyrics === KIPolicies.FULL || t.metadata.aiUsageLyrics === '')),
    );

    if (allFull) {
      return getKiPoliciesLabels(KIPolicies.FULL);
    } else {
      return getKiPoliciesLabels(KIPolicies.PARTLY);
    }
  }
});

export const selectFrontCover = createSelector(
  valueSelectors.productData.coverImages,
  (images) => images.find((c) => c.type === CoverType.FRONT)?.file,
);

export const selectExistingCovers = createSelector(valueSelectors.productData.coverImages, (images) =>
  images.map((c) => getCoverLabel(c.type)).join(', '),
);

export const selectDefinedGenres = createSelector(valueSelectors.productData.genre, (genre) => genre.filter(isDefined));
export const selectDefinedBisacGenres = createSelector(valueSelectors.productData.bisacGenre, (genre) =>
  genre.filter(isDefined),
);
export const selectDefinedWgsGenres = createSelector(valueSelectors.productData.wgsGenre, (genre) =>
  genre.filter(isDefined),
);

export const selectContributorSummary = createSelector(
  selectMainContributors,
  constraintSelectors.roles,
  (contributors, allRoles) =>
    contributors.map(({ id, contributor, roles }) => ({
      id,
      name: contributor.name,
      roleList: roles.map((id) => allRoles.find((r) => r.id === id)?.name ?? id).join(', '),
    })),
);

export const isSpokenWord = createSelector(
  valueSelectors.productType,
  (type) => type === 'AUDIOBOOK' || type === 'RADIO_PLAY',
);

// undefined if not available
export const normalizedChapter = createSelector(
  isSpokenWord,
  valueSelectors.productData.language,
  (spokenWord, language) => {
    if (!spokenWord) {
      return undefined;
    }

    switch (language) {
      case 'en':
        return 'Chapter';
      case 'de':
        return 'Kapitel';
      default:
        return undefined;
    }
  },
);

export const maxAmountGenres = createSelector(isSpokenWord, (isSpokenWord: boolean) => (isSpokenWord ? 1 : 3));

export const selectProductDomain = createSelector(valueSelectors.productType, (type) => {
  switch (type) {
    case 'AUDIOBOOK':
      return 'AUDIO-BOOK';
    case 'RADIO_PLAY':
      return 'RADIO-PLAY';
    default:
      return 'MUSIC';
  }
});

export function combineAddableProductTitle(title: string, titleVersion: string) {
  const mainPart = title || $localize`:@@product-wizard.session.no-title:<No Title>`;

  if (titleVersion) {
    return ` - ${mainPart} (${titleVersion})`;
  }

  return ` - ${mainPart}`;
}

export const addableProductTitle = createSelector(
  valueSelectors.productData.title,
  valueSelectors.productData.titleVersion,
  combineAddableProductTitle,
);

// Spoken word tracks get the product title appended. Kept separate, so product title edits don't affect music.
const trackTitleSuffix = createSelector(
  isSpokenWord,
  valueSelectors.tracksProductTitleAdded,
  addableProductTitle,
  (isSpoken, addTitle, addableTitle) => (isSpoken && addTitle ? addableTitle : ''),
);

const trackSummary = memoizePerItem(
  (
    track: ReturnType<typeof selectTracksWithContributors>[number],
    isSpoken: boolean,
    titleSuffix: string,
    existingData: ReturnType<typeof wizardFeature.selectExistingTrackData>[number] | undefined,
    allRoles: ReturnType<typeof constraintSelectors.roles>,
  ): TrackSummary => {
    let contributors: ContributorAssignmentAndEntry[];
    let metadata: TrackMetadata;

    if (track.existing) {
      contributors = typeof existingData === 'object' ? existingData.contributors : [];
      metadata = typeof existingData === 'object' ? existingData.metadata : defaultTrackMetadata;
    } else {
      contributors = track.contributors;
      metadata = track.metadata;
    }

    const id = track.id;
    const title = track.title + titleSuffix;

    const aiUsageLyrics = metadata.aiUsageToggle ? metadata.aiUsageLyrics : KIPolicies.NONE;
    const aiUsageAudio = metadata.aiUsageToggle ? metadata.aiUsageAudio : KIPolicies.NONE;
    const aiUsageComposition = metadata.aiUsageToggle ? metadata.aiUsageComposition : KIPolicies.NONE;
    const aiUsageVideo = metadata.aiUsageToggle ? metadata.aiUsageVideo : KIPolicies.NONE;

    const genreArray = isSpoken ? [metadata.genre[0], metadata.bisacGenre[0], metadata.wgsGenre[0]] : metadata.genre;

    return {
      ...track,
      title,
      titleVersion: track.titleVersion ?? '',
      isrc: track.isrc ?? '',
      contributors: contributors.map(({ id, contributor, roles }) => ({
        id,
        name: contributor.name,
        roleList: roles.map((id) => allRoles.find((r) => r.id === id)?.name ?? id).join(', '),
      })),
      id,
      ...metadata,
      aiUsageLyrics: aiUsageLyrics ? getKiPoliciesLabels(aiUsageLyrics) : '',
      aiUsageAudio: aiUsageAudio ? getKiPoliciesLabels(aiUsageAudio) : '',
      aiUsageComposition: aiUsageComposition ? getKiPoliciesLabels(aiUsageComposition) : '',
      aiUsageVideo: aiUsageVideo ? getKiPoliciesLabels(aiUsageVideo) : '',
      genres: genreArray.filter(isDefined),
      hasLyrics: metadata.lyrics.length > 0,
    };
  },
);

export const selectTrackSummaries = createSelector(
  selectTracksWithContributors,
  isSpokenWord,
  trackTitleSuffix,
  wizardFeature.selectExistingTrackData,
  constraintSelectors.roles,
  (tracks, isSpoken, titleSuffix, existingTrackData, allRoles): TrackSummary[] =>
    tracks.map((track) =>
      trackSummary(track, isSpoken, titleSuffix, track.existing ? existingTrackData[track.id] : undefined, allRoles),
    ),
);

export const shopsResourceDependencies = createSelector(
  valueSelectors.id,
  valueSelectors.productType,
  valueSelectors.productData,
  nonPendingTracks,
  valueSelectors.tracksProductTitleAdded,
  valueSelectors.tracksPreview,
  valueSelectors.releaseDates,
  valueSelectors.contributors,
  valueSelectors.productContributors,
  valueSelectors.overallMetadata,
  valueSelectors.countrySelection,
  valueSelectors.neighbouringRightsSelection,
  valueSelectors.contractSelection,
  (
    id,
    productType,
    productData,
    tracklisting,
    tracksProductTitleAdded,
    tracksPreview,
    releaseDates,
    contributors,
    productContributors,
    overallMetadata,
    countrySelection,
    neighbouringRightsSelection,
    contractSelection,
  ): Omit<DmbWizardValue, 'lastModified' | 'eanWasGenerated' | 'stepProgress'> => ({
    id,
    productType,
    productData,
    tracklisting,
    tracksProductTitleAdded,
    tracksPreview,
    releaseDates,
    contributors,
    productContributors,
    overallMetadata,
    countrySelection,
    neighbouringRightsSelection,
    contractSelection,
    shopSelection: { allShops: false, shopSelection: [], sendToNewShops: false },
  }),
);

export const trackToAnalyze = createSelector(
  nonPendingTracks,
  wizardFeature.selectAsyncValidations,
  (tracks, asyncValidations) =>
    tracks.find(
      (t): t is NewTrack & { uploadInfo: { status: 'done' } } =>
        !t.existing && t.uploadInfo.status === 'done' && !asyncValidations.trackWasAnalyzed[t.id],
    ),
);

export const hasBookletSelector = createSelector(valueSelectors.productData.coverImages, (images) =>
  images.some((image) => image.type === CoverType.BOOKLET),
);

export const scriptLanguagesWithNone = createSelector(constraintSelectors.scriptLanguages, (langs) => {
  if (langs.length === 0) {
    return langs;
  }

  return [{ value: '', label: $localize`:@@Common.select.no-selection:No selection` }, ...langs];
});

function validations<T>(...validationFn: ((value: T) => null | string)[]): (value: T) => null | string[] {
  return (value) => {
    const errorMsgs: string[] = [];

    validationFn.forEach((vFn) => {
      const result = vFn(value);
      if (result) {
        errorMsgs.push(result);
      }
    });

    return errorMsgs.length > 0 ? errorMsgs : null;
  };
}

export const productTypeValidity = createSelector(valueSelectors.productType, (type) =>
  type === undefined ? Validity.INVALID : Validity.VALID,
);

const titleBackendErrors = createSelector(wizardFeature.selectAsyncValidations, (value) => value.productTitle.errors);
export const titlePending = createSelector(wizardFeature.selectAsyncValidations, (value) => value.productTitle.pending);
export const titleWarnings = createSelector(
  wizardFeature.selectAsyncValidations,
  (value) => value.productTitle.warnings,
);

const titleVersionBackendErrors = createSelector(
  wizardFeature.selectAsyncValidations,
  (value) => value.productTitleVersion.errors,
);
export const titleVersionPending = createSelector(
  wizardFeature.selectAsyncValidations,
  (value) => value.productTitleVersion.pending,
);
export const titleVersionWarnings = createSelector(
  wizardFeature.selectAsyncValidations,
  (value) => value.productTitleVersion.warnings,
);

const coverImagesBackendErrors = createSelector(
  wizardFeature.selectAsyncValidations,
  (value) => value.coverImages.allErrors,
);
const coverImagesPending = createSelector(
  wizardFeature.selectAsyncValidations,
  valueSelectors.productData.coverImages,
  wizardFeature.selectUploadProgress,
  (value, images, progress) =>
    value.coverImages.pending ||
    images.some((i) => i.uploadInfo.status !== 'done' && progress[i.id]?.error === undefined),
);

const eanUpcBackendErrors = createSelector(wizardFeature.selectAsyncValidations, (value) => value.eanUpc.errors);
export const eanUpcPending = createSelector(wizardFeature.selectAsyncValidations, (value) => value.eanUpc.pending);

export const productDataValidations = {
  title: createSelector(
    valueSelectors.productData.title,
    valueSelectors.productData.language,
    valueSelectors.productData.titleVersion,
    titleBackendErrors,
    (title, language, titleVersion, backendErrors) => {
      const frontendErrors = validations(
        requiredString,
        noEmoji,
        (value) => validTitle(value, language),
        (value) => unequalVersionTitle(value, titleVersion),
      )(title);

      return combineLists(frontendErrors, backendErrors);
    },
  ),
  titleVersion: createSelector(
    valueSelectors.productData.titleVersion,
    titleVersionBackendErrors,
    (titleVersion, backendErrors) => {
      const frontendErrors = validations(validTitleVersion, noEmoji)(titleVersion);

      return combineLists(frontendErrors, backendErrors);
    },
  ),
  coverImages: createSelector(
    valueSelectors.productData.coverImages,
    coverImagesBackendErrors,
    wizardFeature.selectUploadProgress,
    (images, backendErrors, uploadProgress) => {
      const overall = validations(hasFrontcover)(images);

      const entries = images.map((image): null | string[] => {
        const asyncValidation = backendErrors.find((b) => b.id === image.id)?.errors ?? null;
        const uploadErrors = errorMessageFor(uploadProgress[image.id]?.error);
        const allAsyncErrors = combineLists(asyncValidation, uploadErrors);

        if (image.file === undefined) {
          return [
            $localize`:@@product-wizard.file-missing:File saving failed. Please delete this entry and add the file again.`,
          ];
        }

        switch (image.type) {
          case CoverType.FRONT:
          case CoverType.BACK:
            return combineLists(validations(isJpeg, hasAiUsage)(image), allAsyncErrors);
          case CoverType.PROMO:
            return combineLists(validations(isJpegOrPdf)(image.file), allAsyncErrors);
          case CoverType.BOOKLET:
            return combineLists(validations(isPdf)(image.file), allAsyncErrors);
          case CoverType.SCREENSHOT:
          case CoverType.OTHER:
          default:
            return allAsyncErrors;
        }
      });

      return { overall, entries };
    },
  ),
  label: createSelector(valueSelectors.productData.label, validations(requiredString)),
  eanUpc: createSelector(valueSelectors.productData.eanUpc, eanUpcBackendErrors, (value, backendErrors) =>
    validations(requiredString, minLength(12), maxLength(13), validEanUpc, () => backendErrors)(value),
  ),
  catalogNumber: createSelector(
    valueSelectors.productData.catalogNumber,
    validations(minLength(3), validCatalogNumber),
  ),
  language: createSelector(
    valueSelectors.productData.language,
    constraintSelectors.scriptLanguages,
    (selection, langList) => validations(requiredString, selectionAvailable(langList))(selection),
  ),
  genre: createSelector(valueSelectors.productData.genre, validations(genreSelected)),
  pYear: createSelector(
    valueSelectors.productData.pYear,
    validations(requiredString, min(1900), max(new Date().getFullYear() + 1), wholeNumber),
  ),
  pText: createSelector(valueSelectors.productData.pText, validations(requiredString, minLength(2))),
  cYear: createSelector(
    valueSelectors.productData.cYear,
    validations(requiredString, min(1900), max(new Date().getFullYear() + 1), wholeNumber),
  ),
  cText: createSelector(valueSelectors.productData.cText, validations(requiredString, minLength(2))),
  description: createSelector(valueSelectors.productData.description, isSpokenWord, (value, spokenWord) =>
    spokenWord ? validations(requiredString, maxLength(4000), minLength(150))(value) : null,
  ),
  price: createSelector(valueSelectors.productData.price, isSpokenWord, (price, spokenWord) =>
    spokenWord ? null : validations(requiredString, selectionAvailable(pricecodeOptions))(price),
  ),
  // Spoken word specific fields
  seriesName: createSelector(valueSelectors.productData.seriesName, () => null),
  seriesNumber: createSelector(
    valueSelectors.productData.seriesNumber,
    isSpokenWord,
    (seriesNumber: string, isSpokenWord: boolean) =>
      isSpokenWord ? validations(seriesNumberOrRange)(seriesNumber) : null,
  ),
  bookletTitle: createSelector(
    valueSelectors.productData.bookletTitle,
    constraintSelectors.bookletCLineRequired,
    hasBookletSelector,
    (bookletTitle, isRequired, hasBooklet) =>
      isRequired && hasBooklet ? validations(requiredString)(bookletTitle) : null,
  ),
  bookletCYear: createSelector(
    valueSelectors.productData.bookletCYear,
    constraintSelectors.bookletCLineRequired,
    hasBookletSelector,
    (bookletCYear, isRequired, hasBooklet) => {
      if (!hasBooklet) {
        return null;
      }

      if (isRequired) {
        return validations(requiredString, min(1900), max(new Date().getFullYear() + 1), wholeNumber)(bookletCYear);
      }

      return validations(min(1900), max(new Date().getFullYear() + 1), wholeNumber)(bookletCYear);
    },
  ),
  bookletCText: createSelector(
    valueSelectors.productData.bookletCText,
    constraintSelectors.bookletCLineRequired,
    hasBookletSelector,
    (bookletCText, isRequired, hasBooklet) =>
      isRequired && hasBooklet ? validations(requiredString, minLength(2))(bookletCText) : null,
  ),
  bisacGenre: createSelector(valueSelectors.productData.bisacGenre, isSpokenWord, (genre, isSpokenWord) =>
    isSpokenWord ? validations(genreSelected)(genre) : null,
  ),
  wgsGenre: createSelector(valueSelectors.productData.wgsGenre, isSpokenWord, (genre, isSpokenWord) =>
    isSpokenWord ? validations(genreSelected)(genre) : null,
  ),
  ageRating: createSelector(
    valueSelectors.productData.ageRating,
    isSpokenWord,
    constraintSelectors.ageRatingRequired,
    (ageRating: string, isSpokenWord, isRequired) =>
      isSpokenWord && isRequired ? validations(requiredAgeRating)(ageRating) : null,
  ),
  version: createSelector(valueSelectors.productData.version, isSpokenWord, (version: string, isSpokenWord: boolean) =>
    isSpokenWord ? validations(requiredString)(version) : null,
  ),
  reviewQuotes: createSelector(
    valueSelectors.productData.reviewQuotes,
    isSpokenWord,
    (reviewQuotes: string, isSpokenWord: boolean) => (isSpokenWord ? validations(maxLength(4000))(reviewQuotes) : null),
  ),
  keywords: createSelector(
    valueSelectors.productData.keywords,
    isSpokenWord,
    (keywords: string, isSpokenWord: boolean) => (isSpokenWord ? validations(maxLength(4000))(keywords) : null),
  ),
  digitalPrice: createSelector(valueSelectors.productData.digitalPrice, isSpokenWord, (dPrice, isSpokenWord) =>
    isSpokenWord ? validations(requiredString, isNumber, min(0.01), max(99.99), max2Decimals)(dPrice) : null,
  ),
};

const productDataIsPending = createSelector(
  eanUpcPending,
  titlePending,
  titleVersionPending,
  coverImagesPending,
  constraintSelectors.loading,
  (...pendings) => pendings.some(Boolean),
);

const { coverImages: productDataCoverImagesValidation, ...normalProductDataValidations } = productDataValidations;
export const productDataValidity = createSelector(
  productDataIsPending,
  productDataCoverImagesValidation,
  ...Object.values(normalProductDataValidations),
  (
    pending,
    coverImages: { overall: null | string[]; entries: (null | string[])[] },
    ...results: (null | string[])[]
  ) => {
    if (pending) {
      return Validity.PENDING;
    }

    const isValid =
      results.every(entryIsValid) && entryIsValid(coverImages.overall) && coverImages.entries.every(entryIsValid);

    return isValid ? Validity.VALID : Validity.INVALID;
  },
);

const tracklistingAsyncErrors = createSelector(wizardFeature.selectAsyncValidations, (value) => value.tracks);
const trackUploadRunning = createSelector(nonPendingTracks, wizardFeature.selectUploadProgress, (tracks, progress) =>
  tracks.some((t) => !t.existing && t.uploadInfo.status !== 'done' && progress[t.id]?.error === undefined),
);
const tracklistingValidationPending = createSelector(
  wizardFeature.selectAsyncValidations,
  constraintSelectors.loading,
  (value, constraintsPending) =>
    constraintsPending || Object.values(value.tracks).some((t) => t?.title.pending || t?.titleVersion.pending),
);
const previewPending = createSelector(
  valueSelectors.tracksPreview,
  wizardFeature.selectAsyncValidations,
  wizardFeature.selectUploadProgress,
  (preview, asyncValidations, uploadProgress) => {
    if (!preview) {
      return false;
    }

    if (asyncValidations.tracksPreview.pending) {
      return true;
    }

    const noError = uploadProgress[preview.id]?.error === undefined;
    return preview.uploadInfo.status !== 'done' && noError;
  },
);

export const tracklistingValidations = {
  overall: createSelector(constraintSelectors.minTrackCount, nonPendingTracks, trackSelected),
  entries: createSelector(
    nonPendingTracks,
    tracklistingAsyncErrors,
    wizardFeature.selectUploadProgress,
    (tracks, asyncErrors, uploadProgress) => {
      return tracks.map(
        (
          t,
        ): {
          isrc: { errors: string[] | null; pending: boolean };
          title: { errors: string[] | null; warnings: string[] | null; pending: boolean };
          titleVersion: { errors: string[] | null; warnings: string[] | null; pending: boolean };
          general: { errors: string[] | null; pending: boolean };
        } => {
          if (t.existing) {
            return {
              isrc: { errors: null, pending: false },
              title: { errors: null, warnings: null, pending: false },
              titleVersion: { errors: null, warnings: null, pending: false },
              general: { errors: null, pending: false },
            };
          }

          const asyncEntry = asyncErrors[t.id];
          const uploadErrors = errorMessageFor(uploadProgress[t.id]?.error);

          const localGeneralErrors =
            t.file === undefined
              ? [
                  $localize`:@@product-wizard.file-missing:File saving failed. Please delete this entry and add the file again.`,
                ]
              : null;

          const generalErrors = combineLists(uploadErrors, localGeneralErrors);

          const isrcErrors = validations(requiredString, validISRC, () => asyncEntry?.isrc.error ?? null)(t.isrc);

          const titleErrors = validations(
            requiredString,
            noEmoji,
            (value) => validTitle(value, t.metadata.titleLanguage),
            (value) => unequalVersionTitle(value, t.titleVersion),
          )(t.title);

          const titleVersionErrors = validations(validTitleVersion, noEmoji)(t.titleVersion);

          if (asyncEntry) {
            return {
              isrc: { ...asyncEntry.isrc, errors: isrcErrors },
              title: {
                ...asyncEntry.title,
                errors: combineLists(asyncEntry.title.errors, titleErrors),
              },
              titleVersion: {
                ...asyncEntry.titleVersion,
                errors: combineLists(asyncEntry.titleVersion.errors, titleVersionErrors),
              },
              general: {
                ...asyncEntry.general,
                errors: combineLists(asyncEntry.general.errors, generalErrors),
              },
            };
          }

          return {
            isrc: { errors: isrcErrors, pending: false },
            title: { errors: null, warnings: null, pending: false },
            titleVersion: { errors: null, warnings: null, pending: false },
            general: { errors: generalErrors, pending: false },
          };
        },
      );
    },
  ),
  preview: createSelector(wizardFeature.selectAsyncValidations, isSpokenWord, (asyncValidations, spokenWord) =>
    spokenWord ? asyncValidations.tracksPreview.errors : null,
  ),
};

export const tracklistingValidity = createSelector(
  tracklistingValidations.overall,
  tracklistingValidations.entries,
  tracklistingValidations.preview,
  tracklistingValidationPending,
  trackUploadRunning,
  previewPending,
  wizardFeature.selectTrackDeletePending,
  (overall, entries, preview, pending, uploadRunning, pPending, pendingDeletes) => {
    if (
      overall !== null ||
      preview !== null ||
      entries.some(
        (e) => !entryIsValid(e.title.errors) || !entryIsValid(e.titleVersion.errors) || !entryIsValid(e.general.errors),
      )
    ) {
      return Validity.INVALID;
    }

    if (pending || uploadRunning || pPending || pendingDeletes.length > 0) {
      return Validity.PENDING;
    }

    return Validity.VALID;
  },
);

function invalidContributor(
  entry: ContributorAssignmentAndEntry,
  availableRoles: { id: string }[],
  rolesRequiringStoreIds: string[],
  currentClientId: string,
): boolean {
  if (entry.roles.length === 0) {
    return true;
  }

  if (entry.contributor.clientId !== currentClientId) {
    return true;
  }

  if (entry.roles.some((id) => availableRoles.every((r) => r.id !== id))) {
    return true;
  }

  if (entry.roles.some((id) => rolesRequiringStoreIds.includes(id))) {
    return entry.contributor.spotifyArtistId === undefined || entry.contributor.appleArtistId === undefined;
  }

  return false;
}

// This one is different, because the design for the error messages is a bit different as well
export const contributorsValidity = createSelector(
  selectMainContributors,
  selectTracksWithContributors,
  constraintSelectors.roles,
  constraintSelectors.rolesRequiringStoreIds,
  constraintSelectors.loading,
  constraintSelectors.clientId,
  (mainContributors, tracks, availableRoles, rolesRequiringStoreIds, constraintsPending, clientId) => {
    if (constraintsPending) {
      return Validity.PENDING;
    }

    if (mainContributors.some((c) => invalidContributor(c, availableRoles, rolesRequiringStoreIds, clientId))) {
      return Validity.INVALID;
    }

    if (
      tracks.some(
        (t) =>
          !t.existing &&
          t.contributors.some((c) => invalidContributor(c, availableRoles, rolesRequiringStoreIds, clientId)),
      )
    ) {
      return Validity.INVALID;
    }

    return Validity.VALID;
  },
);

// We can't easily select nested TrackMetadata, so we only do a shallow select here and pass all errors together
function validateNestedTrackMetadata(
  metadata: CommonTrackMetadata,
  needsExplicit: boolean,
  audioLangList: { value: string }[],
  scriptLangList: { value: string }[],
  isSpoken: boolean,
  productType: ProductType | undefined,
) {
  return {
    genre: validations(genreSelected)(metadata.genre),
    bisacGenre: isSpoken ? validations(genreSelected)(metadata.bisacGenre) : null,
    wgsGenre: isSpoken ? validations(genreSelected)(metadata.wgsGenre) : null,
    titleLanguage: validations(requiredString, selectionAvailable(audioLangList))(metadata.titleLanguage),
    lyricsLanguage: (metadata.instrumental
      ? validations(isInstrumentalConform)
      : validations(requiredString, selectionAvailable(scriptLangList)))(metadata.lyricsLanguage),
    explicit: validations<Explicitness>(requiredString, (value) => (needsExplicit ? isExplicit(value) : null))(
      metadata.explicit,
    ),
    pYear: validations(requiredString, min(1900), max(2099), wholeNumber)(metadata.pYear),
    pText: validations(requiredString, minLength(2))(metadata.pText),
    publisher: {
      overall: validations(notEmpty)(metadata.publisher),
      entries: metadata.publisher.map(validations(requiredString, validPublisher)),
    },
    remasteredYear:
      metadata.remastered && productType !== 'AUDIOBOOK'
        ? validations(requiredString, min(1900), max(new Date().getFullYear()), wholeNumber)(metadata.remasteredYear)
        : null,
  };
}

// Should be the same as validateNestedTrackMetadata, except only explicit is required
function validateOverallTrackMetadata(
  metadata: CommonTrackMetadata,
  needsExplicit: boolean,
  audioLangList: { value: string }[],
  scriptLangList: { value: string }[],
  productType: ProductType | undefined,
) {
  return {
    genre: null,
    bisacGenre: null,
    wgsGenre: null,
    titleLanguage: validations(selectionAvailable(audioLangList))(metadata.titleLanguage),
    lyricsLanguage: validations(selectionAvailable(scriptLangList))(metadata.lyricsLanguage),
    explicit: validations<Explicitness>(requiredString, (value) => (needsExplicit ? isExplicit(value) : null))(
      metadata.explicit,
    ),
    pYear: validations(min(1900), max(2099), wholeNumber)(metadata.pYear),
    pText: validations(minLength(2))(metadata.pText),
    publisher: {
      overall: null,
      entries: metadata.publisher.map(validations(validPublisher)),
    },
    remasteredYear:
      metadata.remastered && productType !== 'AUDIOBOOK'
        ? validations(min(1900), max(new Date().getFullYear()), wholeNumber)(metadata.remasteredYear)
        : null,
  };
}

export function commonTrackMetadataIsValid({
  publisher,
  ...rest
}: ReturnType<typeof validateNestedTrackMetadata>): boolean {
  return (
    Object.values(rest).every(entryIsValid) && entryIsValid(publisher.overall) && publisher.entries.every(entryIsValid)
  );
}

const needsExplicit = createSelector(
  wizardFeature.selectAsyncValidations,
  (value) => value.productTitle.explicit || value.productTitleVersion.explicit,
);

const trackMetadataValidation = memoizePerItem(
  (
    t: NewTrack,
    trackNeedsExplicit: boolean,
    audioLangList: ReturnType<typeof constraintSelectors.audioLanguages>,
    scriptLangList: ReturnType<typeof constraintSelectors.scriptLanguages>,
    isSpoken: boolean,
    productType: ReturnType<typeof valueSelectors.productType>,
    hasVideo: boolean,
  ) => {
    const duration = t.formatInfos?.duration ?? t.metadata.duration;

    return {
      ...validateNestedTrackMetadata(
        t.metadata,
        trackNeedsExplicit,
        audioLangList,
        scriptLangList,
        isSpoken,
        productType,
      ),
      snippetStart: validations(isNumber, min(0), duration ? max(duration) : () => null)(t.metadata.snippetStart),
      snippetDuration: validations(
        isNumber,
        min(30),
        max(60),
        staysInSong(t.metadata.snippetStart, duration),
      )(t.metadata.snippetDuration),
      aiUsageLyrics:
        t.metadata.aiUsageToggle && !t.metadata.instrumental
          ? validations(requiredString)(t.metadata.aiUsageLyrics)
          : null,
      aiUsageAudio: t.metadata.aiUsageToggle ? validations(requiredString)(t.metadata.aiUsageAudio) : null,
      aiUsageComposition: t.metadata.aiUsageToggle ? validations(requiredString)(t.metadata.aiUsageComposition) : null,
      aiUsageVideo: t.metadata.aiUsageToggle && hasVideo ? validations(requiredString)(t.metadata.aiUsageVideo) : null,
    };
  },
);

export const trackMetadataValidations = {
  commonMetadata: createSelector(
    valueSelectors.overallMetadata,
    needsExplicit,
    constraintSelectors.audioLanguages,
    constraintSelectors.scriptLanguages,
    valueSelectors.productType,
    validateOverallTrackMetadata,
  ),
  appleEmail: createSelector(
    valueSelectors.overallMetadata.appleDigitalMastered,
    valueSelectors.overallMetadata.appleEmail,
    (flag, email) => (flag === false ? null : validations(requiredString)(email)),
  ),
  trackMetadata: createSelector(
    nonPendingTracks,
    wizardFeature.selectAsyncValidations,
    constraintSelectors.audioLanguages,
    constraintSelectors.scriptLanguages,
    isSpokenWord,
    valueSelectors.productType,
    selectHasVideo,
    (tracks, asyncValidations, audioLangList, scriptLangList, isSpoken, productType, hasVideo) => {
      const result: Record<string, ReturnType<typeof trackMetadataValidation>> = {};

      tracks.forEach((t) => {
        if (t.existing) {
          return;
        }

        const validationEntry = asyncValidations.tracks[t.id];
        const trackNeedsExplicit =
          validationEntry === undefined
            ? false
            : validationEntry.title.explicit || validationEntry.titleVersion.explicit;

        result[t.id] = trackMetadataValidation(
          t,
          trackNeedsExplicit,
          audioLangList,
          scriptLangList,
          isSpoken,
          productType,
          hasVideo,
        );
      });

      return result;
    },
  ),
};

export const trackMetadataValidity = createSelector(
  trackMetadataValidations.commonMetadata,
  trackMetadataValidations.appleEmail,
  trackMetadataValidations.trackMetadata,
  constraintSelectors.loading,
  (common, appleEmail, tracks, pending) => {
    if (pending) {
      return Validity.PENDING;
    }

    const isValid =
      appleEmail === null &&
      commonTrackMetadataIsValid(common) &&
      Object.values(tracks).every(({ ...common }) => commonTrackMetadataIsValid(common));

    return isValid ? Validity.VALID : Validity.INVALID;
  },
);

export const releaseValidations = {
  releaseDate: createSelector(valueSelectors.releaseDates.releaseDate, validations(requiredDate, notInThePast)),
  origReleaseDate: createSelector(
    valueSelectors.releaseDates.origReleaseDate,
    valueSelectors.releaseDates.origReleaseDateToggle,
    (val, toggle) => (toggle ? validations(requiredDate)(val) : null),
  ),
  tikTokReleaseDate: createSelector(
    valueSelectors.releaseDates.tikTokReleaseDate,
    valueSelectors.releaseDates.tikTokReleaseDateToggle,
    isSpokenWord,
    (val, toggle, spokenWord) => (toggle && !spokenWord ? validations(requiredDate, notInThePast)(val) : null),
  ),
  metaReleaseDate: createSelector(
    valueSelectors.releaseDates.metaReleaseDate,
    valueSelectors.releaseDates.metaReleaseDateToggle,
    isSpokenWord,
    (val, toggle, spokenWord) => (toggle && !spokenWord ? validations(requiredDate, notInThePast)(val) : null),
  ),
  preOrderReleaseDate: createSelector(
    valueSelectors.releaseDates.preOrderReleaseDate,
    valueSelectors.releaseDates.preOrderReleaseDateToggle,
    (val, toggle) => (toggle ? validations(requiredDate, notInThePast)(val) : null),
  ),
  physicalProductReleaseDate: createSelector(
    valueSelectors.releaseDates.physicalProductReleaseDate,
    valueSelectors.releaseDates.physicalProductReleaseDateToggle,
    isSpokenWord,
    (date, toggle, spokenWord) => (toggle && spokenWord ? validations(requiredDate)(date) : null),
  ),
  price: createSelector(
    valueSelectors.releaseDates.price,
    valueSelectors.releaseDates.physicalProductReleaseDateToggle,
    isSpokenWord,
    (value, toggle, spokenWord) =>
      toggle && spokenWord ? validations(requiredString, isNumber, max2Decimals)(value) : null,
  ),
  isbn: createSelector(
    valueSelectors.releaseDates.isbn,
    valueSelectors.releaseDates.physicalProductReleaseDateToggle,
    isSpokenWord,
    (value, toggle, spokenWord) => (toggle && spokenWord ? validations(requiredString, validIsbnFormat)(value) : null),
  ),
  textReleaseDate: createSelector(
    valueSelectors.releaseDates.textReleaseDate,
    valueSelectors.releaseDates.textReleaseDateToggle,
    isSpokenWord,
    (date, toggle, spokenWord) => (toggle && spokenWord ? validations(requiredDate)(date) : null),
  ),
  spokenWordCopyrightReleaseDate: createSelector(
    valueSelectors.releaseDates.spokenWordCopyrightReleaseDate,
    valueSelectors.releaseDates.textReleaseDateToggle,
    isSpokenWord,
    (value, toggle, spokenWord) => (toggle && spokenWord ? validations(requiredString)(value) : null),
  ),
  spokenWordCopyrightYear: createSelector(
    valueSelectors.releaseDates.spokenWordCopyrightYear,
    valueSelectors.releaseDates.textReleaseDateToggle,
    isSpokenWord,
    (value, toggle, spokenWord) =>
      toggle && spokenWord ? validations(requiredString, min(1900), max(2099), wholeNumber)(value) : null,
  ),
  tracks: createSelector(
    nonPendingTracks,
    valueSelectors.releaseDates.preReleaseSongsToggle,
    valueSelectors.releaseDates.preOrderReleaseDate,
    valueSelectors.releaseDates.preOrderReleaseDateToggle,
    isSpokenWord,
    (tracks, toggle, preOrderDate, preOrderToggle, spokenWord) => {
      if (!toggle || tracks.length <= 1 || spokenWord) {
        return tracks.map(() => ({ download: null, streaming: null }));
      }

      return tracks.map((t) => {
        if (preOrderToggle && preOrderDate) {
          return {
            download: notBeforePreOrderDate(t.downloadReleaseDate, preOrderDate),
            streaming: notBeforePreOrderDate(t.streamingReleaseDate, preOrderDate),
          };
        }

        return {
          download: notInThePast(t.downloadReleaseDate),
          streaming: notInThePast(t.streamingReleaseDate),
        };
      });
    },
  ),
  tracksOverall: createSelector(
    nonPendingTracks,
    valueSelectors.releaseDates.preReleaseSongsToggle,
    isSpokenWord,
    (tracks, toggle, spokenWord) =>
      toggle && tracks.length > 1 && !spokenWord ? validNumberOfPrereleases(tracks) : null,
  ),
};

const { tracks: trackReleaseValidation, ...normalReleaseValidations } = releaseValidations;
export const releaseValidity = createSelector(
  trackReleaseValidation,
  ...Object.values(normalReleaseValidations),
  (trackValidity: ReturnType<typeof trackReleaseValidation>, ...rest: (string[] | null)[]) => {
    if (trackValidity.some((t) => t.download !== null || t.streaming !== null)) {
      return Validity.INVALID;
    }

    if (rest.some((r) => r !== null)) {
      return Validity.INVALID;
    }

    return Validity.VALID;
  },
);

export const countryShopsValidations = {
  countries: createSelector(valueSelectors.countrySelection.countries, validations(countrySelected)),
  shops: createSelector(valueSelectors.shopSelection, validations(shopSelected)),
  neighbouringRights: createSelector(valueSelectors.neighbouringRightsSelection, (selection) => {
    if (!selection.evaluate) {
      return {};
    }

    const result: Record<
      string,
      { percentage: string[] | null; startDate: string[] | null; endDate: string[] | null } | undefined
    > = {};

    selection.countries.forEach((id) => {
      const assignment = selection.assignments.find((a) => a.id === id);
      result[id] = {
        percentage: validations(requiredString, isNumber, min(0), max(100))(assignment?.percentage ?? ''),
        startDate: validations(requiredDate)(assignment?.startDate),
        endDate: validations(requiredDate)(assignment?.endDate),
      };
    });

    return result;
  }),
};

const { neighbouringRights: neighbouringRightsValidation, ...normalCountryShopsValidations } = countryShopsValidations;
export const countryShopsValidity = createSelector(
  neighbouringRightsValidation,
  ...Object.values(normalCountryShopsValidations),
  (neighbouringRights: ReturnType<typeof neighbouringRightsValidation>, ...rest: (null | string[])[]) => {
    if (rest.some((r) => r !== null)) {
      return Validity.INVALID;
    }

    if (
      Object.values(neighbouringRights).some(
        (v) =>
          v !== undefined && (!entryIsValid(v.percentage) || !entryIsValid(v.startDate) || !entryIsValid(v.endDate)),
      )
    ) {
      return Validity.INVALID;
    }

    return Validity.VALID;
  },
);

export const overallValidity = createSelector(
  productTypeValidity,
  productDataValidity,
  tracklistingValidity,
  contributorsValidity,
  trackMetadataValidity,
  releaseValidity,
  (...validities) => {
    if (validities.includes(Validity.PENDING)) {
      return Validity.PENDING;
    }

    if (validities.includes(Validity.INVALID)) {
      return Validity.INVALID;
    }

    return Validity.VALID;
  },
);

const anyUploadErros = createSelector(
  valueSelectors.productData.coverImages,
  nonPendingTracks,
  wizardFeature.selectUploadProgress,
  (images, tracks, progress) => {
    if (images.some((i) => progress[i.id]?.error !== undefined)) {
      return true;
    }

    return tracks.some((t) => progress[t.id]?.error !== undefined);
  },
);

export const saveDenyingProblem = createSelector(
  valueSelectors.productType,
  productDataValidations.title,
  productDataValidations.eanUpc,
  anyUploadErros,
  overallValidity,
  (type, titleResult, eanUpcResult, hasUploadErrors, overallValidity) => {
    if (overallValidity === Validity.PENDING) {
      return $localize`:@@product-wizard.saving-problem.pending:Something is pending`;
    }

    const titleInvalid = !entryIsValid(titleResult);
    const typeInvalid = type === undefined;
    const eanUpcInvalid = !entryIsValid(eanUpcResult);

    if (titleInvalid && (typeInvalid || eanUpcInvalid)) {
      return $localize`:@@product-wizard.saving-problem.invalid-main-value:Please enter a product title or an EAN/UPC and a product type`;
    }

    if (hasUploadErrors) {
      return $localize`:@@product-wizard.saving-problem.upload-errors:At least one upload has failed`;
    }

    return null;
  },
);

export const deliveryDenyingProblem = createSelector(overallValidity, (overallValidity) =>
  overallValidity === Validity.VALID
    ? null
    : $localize`:@@product-wizard.delivery-problem.errors:Please rectify all errors`,
);

export const anythingPending = createSelector(
  overallValidity,
  (overallValidity) => overallValidity === Validity.PENDING,
);

// Attaching ids to touched, to make sure signals react on session change
export const productDataTouched = createSelector(valueSelectors.stepProgress, valueSelectors.id, (step, id) => {
  const touched = step !== undefined && step > WizardStep.PRODUCT_DATA;
  return { id, touched };
});
export const tracklistingTouched = createSelector(valueSelectors.stepProgress, valueSelectors.id, (step, id) => {
  const touched = step !== undefined && step > WizardStep.TRACKLISTING;
  return { id, touched };
});
export const trackMetadataTouched = createSelector(valueSelectors.stepProgress, valueSelectors.id, (step, id) => {
  const touched = step !== undefined && step > WizardStep.TRACK_METADATA;
  return { id, touched };
});
export const releasedateTouched = createSelector(valueSelectors.stepProgress, valueSelectors.id, (step, id) => {
  const touched = step !== undefined && step > WizardStep.RELEASE;
  return { id, touched };
});
export const countriesShopsTouched = createSelector(valueSelectors.stepProgress, valueSelectors.id, (step, id) => {
  const touched = step !== undefined && step > WizardStep.COUNTRIES_SHOPS;
  return { id, touched };
});
